import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  all,
  call,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  CONTENTTYPE_FILE,
  CONTENTTYPE_TEXT,
  CONTENT_TYPE,
  FILEOPERATION_ADD,
  FILEOPERATION_DELETE,
  HEARINGSTATUS_ACTIVE,
  HEARINGSTATUS_AWAITING_CONCLUSION,
  HEARINGSTATUS_AWAITING_STARTDATE,
  HEARINGSTATUS_CONCLUDED,
  HEARINGSTATUS_DRAFT,
} from '../../constants/api';
import { SORTING_KEYS } from '../../constants/sortingKeys';
import { COMPONENT_REDUCER } from '../../constants/store';
import { comparators } from '../../utilities/comparators';
import { convertDataUrlToFile } from '../../utilities/files';
import {
  mapFileContentDiff,
  mapFileContentWithUrlToDownload,
} from '../../utilities/mapperFunctions';
import {
  removeContentsFromStore,
  updateFieldContentAutoSaveInitialize,
  updateFieldContentInitialize,
} from '../actions/api';
import { contentByHearingIdSelector } from '../apiFeatures/contentSlice';
import { allContentTypesSelector } from '../apiFeatures/contentTypeSlice';
import { allFieldsByHearingTypeIdSelector } from '../apiFeatures/fieldSlice';
import { fieldTemplateByHearingTypeIdSelector } from '../apiFeatures/fieldTemplateSlice';
import { allFieldTypesSelector } from '../apiFeatures/fieldTypeSlice';
import { allFieldTypeSpecificationsSelector } from '../apiFeatures/fieldTypeSpecificationSlice';
import { hearingStatusByStatusSelector } from '../apiFeatures/hearingStatusSlice';
import { hearingTypeByHearingIdSelector } from '../apiFeatures/hearingTypeSlice';
import { baseApiUrlSelector } from '../bootstrapSlice';
import {
  dismissGlobalMessage,
  globalMessageModes,
  parseApiError,
  setGlobalMessage,
} from '../globalMessageSlice';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import { awaitApiCall } from '../helpers/sagaHelpers';

// ---------- ACTIONS -----------
const updateContentDiff = createAction(
  'component/renderHearing/updateContentDiff',
);
const updateContentDiffInternal = createAction(
  'component/renderHearing/updateContentDiff/internal',
);
const updateFieldDataInitialize = createAction(
  'component/renderHearing/updateFieldData/initialize',
);
const autoUpdateFieldData = createAction(
  'component/renderHearing/autoUpdateFieldData',
);
const initializeState = createAction('component/renderHearing/initializeState');
const setIsUpdatingHearing = createAction(
  'component/renderHearing/setIsUpdatingHearing',
);

const confirmNotificationOfHearingUpdates = createAction(
  'component/renderHearing/confirmUpdateActiveHearingText',
);
const declineNotificationOfHearingUpdates = createAction(
  'component/renderHearing/declineUpdateActiveHearingText',
);

const initialState = {
  contentDiffs: [],
  isUpdatingHearing: false,
};

// ---------- REDUCERS -----------
const renderHearingReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(updateContentDiffInternal, (state, action) => {
      state.contentDiffs = action.payload;
    })
    .addCase(initializeState, () => initialState)
    .addCase(setIsUpdatingHearing, (state, action) => {
      state.isUpdatingHearing = action.payload;
    });
});

// ---------- SAGAS -----------
export function* renderHearingSaga() {
  yield all([
    fork(updateContentDiffSaga),
    fork(updateFieldDataSaga),
    fork(autoUpdateFieldDataSaga),
  ]);
}

function* autoUpdateFieldDataSaga() {
  yield takeLatest(autoUpdateFieldData.type, function* (action) {
    const { hearingId, newStatus, cancelAutoUpdate } = action.payload;

    if (cancelAutoUpdate || newStatus !== HEARINGSTATUS_DRAFT) {
      return;
    }

    yield delay(30000);

    yield put(
      updateFieldDataInitialize({
        hearingId,
        newStatus,
        isAutoUpdate: true,
      }),
    );

    yield put(
      autoUpdateFieldData({
        hearingId,
        newStatus,
      }),
    );
  });
}

function* updateFieldDataSaga() {
  yield takeLeading(updateFieldDataInitialize.type, function* (action) {
    const {
      hearingId,
      newStatus,
      confirmNotifyChanges,
      isAutoUpdate,
    } = action.payload;
    yield put(setIsUpdatingHearing(true));
    if (!isAutoUpdate) {
      yield put(autoUpdateFieldData({ cancelAutoUpdate: true }));
    }

    const fieldData = yield select(fieldDataSelector(hearingId));
    const contentDiffs = yield select(contentDiffsSelector);
    const hearingStatus = yield select(
      hearingStatusByStatusSelector(newStatus),
    );

    if (newStatus !== HEARINGSTATUS_DRAFT && isAutoUpdate) {
      return;
    }

    let result = [];
    const contentIdsToDelete = [];

    // create an object that represents the field diffs with mapped operations add/update/delete
    for (let diff of contentDiffs) {
      const currentFieldData =
        fieldData.find((f) => f.id === diff.fieldId) || {};
      const currentFieldType = currentFieldData.type;

      const indexOfFieldType = result.findIndex(
        (f) => f.fieldType === currentFieldType,
      );

      if (diff.contentType === CONTENTTYPE_TEXT) {
        const contentToPush = {
          fieldType: currentFieldType,
          content: diff.content,
          isEmptyContent: !diff.content,
        };
        if (indexOfFieldType === -1) {
          result.push(contentToPush);
        } else {
          result[indexOfFieldType] = {
            ...result[indexOfFieldType],
            ...contentToPush,
          };
        }
      }
      if (diff.contentType === CONTENTTYPE_FILE) {
        if (diff.wasAutoSaved) {
          continue;
        } else if (isAutoUpdate) {
          let newDiff = Object.assign({}, diff, { wasAutoSaved: true });
          yield put(updateContentDiff(newDiff));
        }

        const currentFileContent = currentFieldData.fileContent.find(
          (file) =>
            getAttr(file, 'fileName') === diff.content.fileName ||
            !diff.allowMultiple,
        );

        let fileOperationDelete = null;
        let fileOperationAdd = null;
        const isUpdateOperation = !diff.isDeleteOperation;
        if (currentFileContent) {
          contentIdsToDelete.push(getEntityId(currentFileContent));
          fileOperationDelete = {
            file: null,
            contentId: getEntityId(currentFileContent),
            operation: FILEOPERATION_DELETE,
          };
        }

        if (!currentFileContent || isUpdateOperation) {
          const file = yield call(
            convertDataUrlToFile,
            diff.content.fileContentUrl,
            diff.content.fileName,
            diff.content.fileType,
          );
          fileOperationAdd = {
            file: file,
            operation: FILEOPERATION_ADD,
          };
        }

        if (!currentFileContent && diff.isDeleteOperation) {
          continue;
        }

        const contentToPush = {
          fieldType: currentFieldType,
          fileOperations: [],
        };
        fileOperationDelete &&
          contentToPush.fileOperations.push(fileOperationDelete);
        fileOperationAdd && contentToPush.fileOperations.push(fileOperationAdd);

        if (indexOfFieldType === -1) {
          result.push(contentToPush);
        } else {
          result[indexOfFieldType] = {
            ...result[indexOfFieldType],
            fileOperations: [
              ...(result[indexOfFieldType].fileOperations || []),
              ...contentToPush.fileOperations,
            ],
          };
        }
      }
    }

    let notifyAboutChanges = false;
    // Some of the fields have changed
    if (result.length > 0) {
      // Prompt user to confirm or decline notification to users about the change
      if (confirmNotifyChanges) {
        yield put(
          setGlobalMessage({
            mode: globalMessageModes.changeActiveHearingText,
          }),
        );

        const raceResult = yield race({
          confirmed: take(confirmNotificationOfHearingUpdates.type),
          declined: take(declineNotificationOfHearingUpdates.type),
        });

        if (raceResult.confirmed) {
          notifyAboutChanges = true;
        }
      }
    }

    if (isAutoUpdate) {
      const updateFieldContentAutoSave = yield put(
        updateFieldContentAutoSaveInitialize({
          id: hearingId,
          dto: result,
          hearingStatusId: getEntityId(hearingStatus),
          notifyAboutChanges: false,
        }),
      );
      const actionResult = yield awaitApiCall(updateFieldContentAutoSave);

      if (actionResult.success) {
        yield put(removeContentsFromStore(contentIdsToDelete));
      }
      yield put(setIsUpdatingHearing(false));
    } else {
      const updateFieldContentInitializeAction = yield put(
        updateFieldContentInitialize({
          id: hearingId,
          dto: result,
          hearingStatusId: getEntityId(hearingStatus),
          notifyAboutChanges: notifyAboutChanges,
        }),
      );
      const updateFieldContentResult = yield awaitApiCall(
        updateFieldContentInitializeAction,
      );
      if (updateFieldContentResult.success) {
        if (
          newStatus === HEARINGSTATUS_AWAITING_STARTDATE ||
          newStatus === HEARINGSTATUS_ACTIVE
        ) {
          yield put(
            setGlobalMessage({ mode: globalMessageModes.hearingPublished }),
          );
          yield take(dismissGlobalMessage);
        } else if (newStatus === HEARINGSTATUS_DRAFT) {
          yield put(
            setGlobalMessage({ mode: globalMessageModes.hearingSaved }),
          );
          yield take(dismissGlobalMessage);
        } else if (newStatus === HEARINGSTATUS_AWAITING_CONCLUSION) {
          yield put(
            setGlobalMessage({ mode: globalMessageModes.conclusionSaved }),
          );
          yield take(dismissGlobalMessage);
        } else if (newStatus === HEARINGSTATUS_CONCLUDED) {
          yield put(
            setGlobalMessage({ mode: globalMessageModes.conclusionFinalized }),
          );
          yield take(dismissGlobalMessage);
        }
        yield put(removeContentsFromStore(contentIdsToDelete));
        yield put(updateContentDiffInternal([]));
        yield put(setIsUpdatingHearing(false));
      } else {
        yield put(setIsUpdatingHearing(false));
        yield put(parseApiError(updateFieldContentResult.error.payload));
      }
    }
  });
}

function* updateContentDiffSaga() {
  yield takeLatest(updateContentDiff.type, function* (action) {
    // Creates a deep copy of contentDiffs from redux-store
    let diffs = JSON.parse(JSON.stringify(yield select(contentDiffsSelector)));
    let newContent = action.payload;

    mapFileContentDiff(newContent);

    let { fieldId, contentType, content, allowMultiple } = newContent;

    if (!Array.isArray(content)) {
      content = [content];
    }

    content.forEach((ele) => {
      const indexOfContent = diffs.findIndex(
        (c) =>
          c.fieldId === fieldId &&
          c.contentType === contentType &&
          (!allowMultiple || c.content.fileName === ele.fileName),
      );
      const contentToPush = Object.assign({}, newContent, { content: ele });
      indexOfContent === -1
        ? diffs.push(contentToPush)
        : (diffs[indexOfContent] = contentToPush);
    });

    yield put(updateContentDiffInternal(diffs));
  });
}

// ---------- SELECTORS -----------
const renderHearingState = (state) =>
  state.component[COMPONENT_REDUCER.RENDER_HEARING];

const contentDiffsSelector = createSelector(
  renderHearingState,
  (state) => state.contentDiffs,
);

const isUpdatingHearingSelector = createSelector(
  renderHearingState,
  (state) => state.isUpdatingHearing,
);

const fieldDataSelector = (hearingId) => (state) => {
  const baseApiUrl = baseApiUrlSelector(state);
  const hearingType = hearingTypeByHearingIdSelector(hearingId)(state);
  const fields = allFieldsByHearingTypeIdSelector(getEntityId(hearingType))(
    state,
  );
  const fieldTypes = allFieldTypesSelector(state);
  const fieldTypeSpecifications = allFieldTypeSpecificationsSelector(state);
  const fieldTemplates = fieldTemplateByHearingTypeIdSelector(
    getEntityId(hearingType),
  )(state);
  const content = contentByHearingIdSelector(hearingId)(state);
  const contentTypes = allContentTypesSelector(state);
  const contentTypeText = contentTypes.find(
    (c) => getAttr(c, 'contentType') === CONTENTTYPE_TEXT,
  );
  const contentTypeFile = contentTypes.find(
    (c) => getAttr(c, 'contentType') === CONTENTTYPE_FILE,
  );

  const sortedFields = fields.sort(
    comparators.renderHearings[SORTING_KEYS.SORT_FIELDS],
  );

  return sortedFields.map((field) => {
    const fieldTypeForField = fieldTypes.find(
      (fT) => getRelId(field, 'fieldType') === getEntityId(fT),
    );

    const fieldTypeSpecificationsForField = fieldTypeSpecifications.filter(
      (fTS) => getAttr(fTS, 'fieldType') === getEntityId(fieldTypeForField),
    );

    const contentForField = content.filter(
      (c) => getRelId(c, 'field') === getEntityId(field),
    );

    const textContentForField = contentForField.filter(
      (c) => getRelId(c, 'contentType') === getEntityId(contentTypeText),
    );

    const fileContentForField = contentForField
      .filter(
        (c) => getRelId(c, 'contentType') === getEntityId(contentTypeFile),
      )
      .map(mapFileContentWithUrlToDownload(baseApiUrl));

    const templatesForField = fieldTemplates.filter(
      (fT) => getRelId(fT, 'field') === getEntityId(field),
    );
    return {
      type: getAttr(fieldTypeForField, 'fieldType'),
      id: getEntityId(field),
      templates: templatesForField,
      textContent: textContentForField,
      fileContent: fileContentForField,
      fieldTypeSpecifications: fieldTypeSpecificationsForField,
      hearingId: hearingId,
    };
  });
};

const fieldDataWithDiffsSelector = (hearingId) =>
  createSelector(
    [fieldDataSelector(hearingId), contentDiffsSelector],
    (fieldData, contentDiffs) => {
      // Creates a deep copy of fieldData to avoid modifying a readonly object directly from redux-store
      let fieldDataWithDiff = JSON.parse(JSON.stringify(fieldData));

      contentDiffs.forEach((diff) => {
        const indexOfField = fieldDataWithDiff.findIndex(
          (field) => field.id === diff.fieldId,
        );

        if (indexOfField === -1) {
          return;
        }

        let currentFieldData = fieldDataWithDiff[indexOfField];
        if (diff.contentType === CONTENTTYPE_TEXT) {
          let [textContent] = currentFieldData.textContent;
          if (textContent) {
            textContent.attributes.textContent = diff.content;
          } else {
            textContent = createNewTextContent(diff.content);
            currentFieldData.textContent.push(textContent);
          }
        }
        if (diff.contentType === CONTENTTYPE_FILE) {
          const indexOfExistingContent = currentFieldData.fileContent.findIndex(
            (c) =>
              getAttr(c, 'fileName') === diff.content.fileName ||
              !diff.allowMultiple,
          );
          if (indexOfExistingContent !== -1) {
            if (!diff.content.isDeleteOperation) {
              let newFileContent =
                currentFieldData.fileContent[indexOfExistingContent];

              newFileContent.attributes.urlToDownload =
                diff.content.fileContentUrl;
              newFileContent.attributes.fileName = diff.content.fileName;
              newFileContent.attributes.fileContentType = diff.content.fileType;

              currentFieldData.fileContent[
                indexOfExistingContent
              ] = newFileContent;
            } else {
              currentFieldData.fileContent = currentFieldData.fileContent.filter(
                (_, index) => index !== indexOfExistingContent,
              );
            }
          } else {
            if (!diff.content.isDeleteOperation) {
              const newFileContent = createNewFileContent(diff.content);
              currentFieldData.fileContent.push(newFileContent);
            }
          }
        }
        fieldDataWithDiff[indexOfField] = currentFieldData;
      });

      return fieldDataWithDiff;
    },
  );

export {
  autoUpdateFieldData,
  confirmNotificationOfHearingUpdates,
  declineNotificationOfHearingUpdates,
  fieldDataWithDiffsSelector,
  initializeState,
  isUpdatingHearingSelector,
  updateContentDiff,
  updateFieldDataInitialize,
};
export default renderHearingReducer;

const createNewTextContent = (value) => {
  return {
    attributes: {
      textContent: value,
    },
    type: CONTENT_TYPE,
  };
};

const createNewFileContent = (value) => {
  return {
    attributes: {
      fileName: value.fileName,
      fileContentType: value.fileType,
      urlToDownload: value.fileContentUrl,
    },
    type: CONTENT_TYPE,
  };
};
